// The second step of signing in: a code from an authenticator app
// (Sahil, 2026-10-23).
//
// After email + password, anyone who can reach the bank or run a company
// types the six-digit code their authenticator app shows (Google
// Authenticator, Authy, 1Password, Apple Passwords...). Supabase calls this a
// "totp" MFA factor; a verified code lifts the session from aal1 to aal2.
//
// This replaced passkeys (WebAuthn) the same day: Supabase refuses to turn
// on WebAuthn as a second factor ("Enabling of MFA with WebAuthn not
// currently supported", Management API, 2026-10-23). Authenticator codes
// are on for both projects.
//
//   REQUIRED  an admin of any company they belong to -- the only people who
//             connect banks (Sahil, 2026-10-23). Asked at EVERY sign-in.
//   OPTIONAL  every other staff member: they may add one in their profile,
//             and once they have one they are asked for it too.
//   NEVER     tenant and owner portal users.
//
// A lost authenticator is reset by an admin (Team & Roles card). There are
// no backup codes, by decision.
//
// Pure rules first (mustUseMfa, mfaGateMode, mfaErrorText) so the tests run
// them without a browser; the Supabase calls are thin wrappers that take the
// client as an argument. This file imports nothing on purpose.

// Roles that do the company's work. Portal roles never see the gate.
const PORTAL_ROLES = new Set(["tenant", "owner"]);

// The factor type this app uses. One place, so a future switch is one edit.
export const FACTOR_TYPE = "totp";

/** True when this person must use a second step to sign in: an admin of
 *  any company (only admins connect banks). memberships: [{ company_id, role }] */
export function mustUseMfa({ memberships } = {}) {
  return (memberships || []).some(m => m && m.role === "admin");
}

/** Does this person work in any company (not just a portal login)? */
export function isStaffAnywhere(memberships) {
  return (memberships || []).some(m => m && m.role && !PORTAL_ROLES.has(m.role));
}

/** What the screen after sign-in must do.
 *    "none"   go straight on (already aal2, a portal user, optional with no
 *             authenticator, or a device trusted in the last 14 days)
 *    "verify" they have an authenticator and this session has not used it yet
 *    "enroll" a second step is required and they have no authenticator yet
 *  trusted: this browser had a code within TRUST_DAYS, from this country
 *  (2026-10-24). A trusted device never skips enrolment. */
export function mfaGateMode({ aal, factors, required, staff, trusted = false } = {}) {
  if (aal === "aal2") return "none";
  if (!staff) return "none";
  if ((factors || []).length > 0) return trusted ? "none" : "verify";
  if (required) return "enroll";
  return "none";
}

export const TRUST_DAYS = 14;

// ─── This browser's trust lives in an HttpOnly cookie the server sets ──────
// (housify_mfa_device, Path=/api). Page scripts never see the token; the
// browser sends it with same-origin calls to /api.

// Any failure (no session, offline, server error) reads as "not trusted":
// the code is asked, never skipped.
async function deviceCall(sb, op) {
  try {
    const { data: { session } = {} } = await sb.auth.getSession();
    if (!session?.access_token) return { ok: false };
    const r = await fetch("/api/account?action=mfa-device", {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: "Bearer " + session.access_token },
      credentials: "same-origin",
      body: JSON.stringify({ op }),
    });
    const j = await r.json().catch(() => ({}));
    return { ok: r.ok, ...j };
  } catch (_) { return { ok: false }; }
}

/** Is this browser trusted right now? { trusted, reason }. A failed check is "not trusted". */
export async function checkTrustedDevice(sb) {
  const r = await deviceCall(sb, "check");
  return { trusted: !!(r.ok && r.trusted), reason: r.reason || "unknown" };
}

/** Right after a good code: remember this browser for TRUST_DAYS. Never throws. */
export async function trustThisDevice(sb) {
  const r = await deviceCall(sb, "trust");
  return !!(r.ok && r.until);
}

/** May this authenticator be removed from the profile? A required person keeps at least one. */
export function canRemoveFactor({ required, factors } = {}) {
  return !(required && (factors || []).length <= 1);
}

/** Six digits, nothing else (spaces people paste are dropped). */
export function normalizeCode(code) {
  return String(code || "").replace(/\D/g, "").slice(0, 6);
}

/** Plain words for whatever Supabase said. */
export function mfaErrorText(err) {
  if (!err) return "";
  const code = String(err.code || "");
  const msg = String(err.message || err);
  if (/_not_enabled$/.test(code) || /\b(totp|mfa)\b.*(not enabled|disabled)|(not enabled|disabled).*\b(totp|mfa)\b/i.test(msg)) {
    return "Authenticator codes are not enabled for this site yet. Ask your administrator.";
  }
  if (code === "mfa_verification_failed" || /invalid totp code/i.test(msg)) {
    return "That code was not accepted. Check the code and the time on your phone, then try again.";
  }
  if (code === "mfa_challenge_expired") return "That code took too long. Enter the code your app shows now.";
  if (code === "mfa_verification_rejected") return "That code was not accepted. Try again.";
  if (code === "mfa_factor_name_conflict") return "You already have an authenticator with that name. Pick another name.";
  if (code === "too_many_enrolled_mfa_factors") return "You have reached the most authenticators an account can hold. Remove one first.";
  if (code === "mfa_factor_not_found") return "That authenticator no longer exists. Sign out and in again.";
  if (code === "insufficient_aal") return "Confirm it's you with your authenticator code first, then try again.";
  if (/too many requests|rate limit/i.test(msg) || code === "over_request_rate_limit") return "Too many tries. Wait a minute, then try again.";
  return "The code step did not finish: " + msg;
}

/** A name for a new authenticator entry: the kind of device and today's date, unique among `taken`. */
export function defaultFactorName(ua, taken = [], now = new Date()) {
  const s = String(ua || "");
  const device = /iPhone/.test(s) ? "iPhone" : /iPad/.test(s) ? "iPad" : /Android/.test(s) ? "Android phone"
    : /Macintosh|Mac OS X/.test(s) ? "Mac" : /Windows/.test(s) ? "Windows PC" : "Authenticator";
  const base = device + " · " + now.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
  const used = new Set((taken || []).map(n => String(n || "").toLowerCase()));
  if (!used.has(base.toLowerCase())) return base;
  for (let i = 2; i < 100; i++) if (!used.has((base + " (" + i + ")").toLowerCase())) return base + " (" + i + ")";
  return base + " (" + now.getTime() + ")";
}

// ─── Supabase calls (the client is passed in) ───────────────────────────

const escapeEmail = (email) => String(email || "").trim().toLowerCase().replace(/[%_\\]/g, c => "\\" + c);

/** { required, staff, error } from the person's active memberships. */
export async function loadMfaRequirement(sb, user) {
  const email = user?.email;
  if (!email) return { required: false, staff: false, error: "" };
  const { data: memberships, error } = await sb.from("company_members")
    .select("company_id, role").ilike("user_email", escapeEmail(email)).eq("status", "active");
  if (error) return { required: false, staff: false, error: error.message || "membership lookup failed" };
  const staff = isStaffAnywhere(memberships);
  if (!staff) return { required: false, staff, error: "" };
  return { required: mustUseMfa({ memberships }), staff, error: "" };
}

/** Verified authenticators, and every totp factor (unverified ones are leftovers). */
async function listFactors(sb) {
  const { data, error } = await sb.auth.mfa.listFactors();
  if (error) return { factors: [], all: [], error };
  const all = (data?.all || []).filter(f => f.factor_type === FACTOR_TYPE);
  return { factors: all.filter(f => f.status === "verified"), all, error: null };
}

/** { aal, nextAal, factors, required, staff, mode, error } for the signed-in person. */
export async function mfaState(sb, user) {
  const { data: lvl, error: aalErr } = await sb.auth.mfa.getAuthenticatorAssuranceLevel();
  if (aalErr) return { aal: null, nextAal: null, factors: [], required: false, staff: false, mode: "error", error: mfaErrorText(aalErr) };
  const aal = lvl?.currentLevel || "aal1";
  const nextAal = lvl?.nextLevel || aal;
  const req = await loadMfaRequirement(sb, user);
  if (req.error) return { aal, nextAal, factors: [], required: false, staff: false, mode: "error", error: req.error };
  const { factors, error: listErr } = await listFactors(sb);
  if (listErr) return { aal, nextAal, factors: [], required: req.required, staff: req.staff, mode: "error", error: mfaErrorText(listErr) };
  // Only worth asking the server when a code would otherwise be asked for.
  let trusted = false, trustReason = "";
  if (aal !== "aal2" && req.staff && factors.length > 0) {
    const t = await checkTrustedDevice(sb);
    trusted = t.trusted; trustReason = t.reason;
  }
  const mode = mfaGateMode({ aal, factors, required: req.required, staff: req.staff, trusted });
  return { aal, nextAal, factors, required: req.required, staff: req.staff, mode, trusted, trustReason, error: "" };
}

/** Step 1 of adding an authenticator: Supabase makes the secret; the app
 *  shows the QR code (an SVG data URI) and the secret for typing by hand.
 *  Nothing is verified yet. Returns { ok, factorId, qrCode, secret, uri, error }. */
export async function startEnrollment(sb, name) {
  const friendlyName = String(name || "").trim().slice(0, 60) || "Authenticator";
  // A setup that was abandoned half way leaves an unverified factor holding
  // its name; clear those first so the next try is not refused.
  const { all } = await listFactors(sb);
  for (const f of all.filter(x => x.status !== "verified")) {
    await sb.auth.mfa.unenroll({ factorId: f.id }).catch(() => {});
  }
  const { data, error } = await sb.auth.mfa.enroll({ factorType: FACTOR_TYPE, friendlyName });
  if (error) return { ok: false, factorId: null, qrCode: "", secret: "", uri: "", error: mfaErrorText(error) };
  const t = data?.totp || {};
  if (!data?.id || !t.secret) return { ok: false, factorId: null, qrCode: "", secret: "", uri: "", error: "The code step did not finish: no secret came back." };
  return { ok: true, factorId: data.id, qrCode: t.qr_code || "", secret: t.secret, uri: t.uri || "", error: "" };
}

/** Step 2: the first code from the app proves the secret landed. Ends at aal2. */
export async function finishEnrollment(sb, factorId, code) {
  return verifyCode(sb, factorId, code);
}

/** Give up on a half-finished setup so its name is free again. */
export async function cancelEnrollment(sb, factorId) {
  if (!factorId) return { ok: true, error: "" };
  const { error } = await sb.auth.mfa.unenroll({ factorId });
  return error ? { ok: false, error: mfaErrorText(error) } : { ok: true, error: "" };
}

/** Confirm it's you with a code (for the first authenticator unless factorId names another). */
export async function verifyCode(sb, factorId, code) {
  const clean = normalizeCode(code);
  if (clean.length !== 6) return { ok: false, error: "Enter the six-digit code from your authenticator app." };
  let id = factorId;
  if (!id) {
    const { factors, error } = await listFactors(sb);
    if (error) return { ok: false, error: mfaErrorText(error) };
    id = factors[0]?.id;
  }
  if (!id) return { ok: false, error: "You have no authenticator set up yet." };
  const { error } = await sb.auth.mfa.challengeAndVerify({ factorId: id, code: clean });
  if (error) return { ok: false, error: mfaErrorText(error) };
  return { ok: true, error: "" };
}

/** Remove one of your own authenticators (the caller checks canRemoveFactor first). */
export async function removeFactor(sb, factorId) {
  const { error } = await sb.auth.mfa.unenroll({ factorId });
  if (error) return { ok: false, error: mfaErrorText(error) };
  return { ok: true, error: "" };
}

/** Admin: remove every authenticator a member of this company has, so they
 *  set a new one up at their next sign-in. Server route: account?action=mfa-reset. */
//  withStepUp (utils/stepUp.js) is passed in, so this file keeps no imports:
//  the admin's own session must have had a code; if not, the prompt opens.
export async function resetMemberMfa(sb, companyId, email, withStepUp) {
  const { data: { session } } = await sb.auth.getSession();
  if (!session?.access_token) return { ok: false, error: "Your session has ended. Sign in again." };
  const send = (token) => fetch("/api/account?action=mfa-reset", {
    method: "POST",
    headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
    body: JSON.stringify({ companyId, email }),
  });
  let resp, json = {};
  try {
    if (withStepUp) {
      const out = await withStepUp(sb, send, "reset someone's authenticator");
      if (out.cancelled) return { ok: false, error: "Enter your authenticator code to reset someone else's." };
      resp = out.res; json = out.data || {};
    } else {
      resp = await send(session.access_token);
      json = await resp.json().catch(() => ({}));
    }
  } catch (e) { return { ok: false, error: "Could not reach the server: " + (e?.message || e) }; }
  if (!resp.ok) return { ok: false, error: json.error || ("HTTP " + resp.status) };
  return { ok: true, removed: Number(json.removed || 0), error: "" };
}
